import { Cart, Order } from '@medusajs/medusa'
import { LiteProductVariant } from '@ortros/stoux-lib'
import { HttpResponse } from 'nexus-res'

import axios from '@/core/axios'

import { AddLineItemDto } from '../dtos/add-line-item.dto'
import { AddShippingMethodForCartDto } from '../dtos/add-shipping-method-for-cart.dto'
import { CompleteCartDto } from '../dtos/complete-cart.dto'
import { CreateCartDto } from '../dtos/create-cart.dto'
import { CreatePaymentSessionDto } from '../dtos/create-payment-session.dto'
import { DeleteLineItemDto } from '../dtos/delete-line-item.dto'
import { GetCartDto } from '../dtos/get-cart.dto'
import { GetVariantByIdDto } from '../dtos/get-variant-by-id.dto'
import { StoreOption } from '../dtos/list-shipping-options-by-cart.dto'
import { SetPaymentSessionDto } from '../dtos/set-payment-session.dto'
import { UpdateCartDto } from '../dtos/update-cart.dto'
import { UpdateLineItemDto } from '../dtos/update-line-item.dto'

type CartResponse = {
    cart: Cart
}

export class CartRepository {
    public async create(dto: CreateCartDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>('/carts', dto)
        return data.cart
    }

    public async getById({ cart_id }: GetCartDto): Promise<Cart> {
        const { data } = await axios.get<CartResponse>(`/carts/${cart_id}`)
        return data.cart
    }

    public async update({ cart_id, ...dto }: UpdateCartDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}`,
            dto,
        )
        return data.cart
    }

    public async addLineItem({
        cart_id,
        ...dto
    }: AddLineItemDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}/line-items`,
            dto,
        )
        return data.cart
    }

    public async updateLineItem({
        cart_id,
        line_id,
        ...dto
    }: UpdateLineItemDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${cart_id}/line-items/${line_id}`,
            dto,
        )
        return data.cart
    }

    public async deleteLineItem({
        cart_id,
        line_id,
    }: DeleteLineItemDto): Promise<Cart> {
        const { data } = await axios.delete<CartResponse>(
            `/carts/${cart_id}/line-items/${line_id}`,
        )
        return data.cart
    }

    public async addShippingMethodForCart(
        dto: AddShippingMethodForCartDto,
    ): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${dto.cart_id}/shipping-methods`,
            {
                option_id: dto.shipping_method_id,
                data: dto.data,
            },
        )
        return data.cart
    }

    async createPaymentSession(dto: CreatePaymentSessionDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${dto.cart_id}/payment-sessions`,
        )
        return data.cart
    }

    async setPaymentSession(dto: SetPaymentSessionDto): Promise<Cart> {
        const { data } = await axios.post<CartResponse>(
            `/carts/${dto.cart_id}/payment-sessions`,
            {
                provider_id: dto.provider_id,
            },
        )
        return data.cart
    }

    public async getVariantById({
        variant_id,
    }: GetVariantByIdDto): Promise<
        HttpResponse<LiteProductVariant & { template_barcode: string }>
    > {
        const { data } = await axios.get<
            HttpResponse<LiteProductVariant & { template_barcode: string }>
        >(`/variants/${variant_id}/lite`)
        return data
    }

    public async complete(dto: CompleteCartDto): Promise<Order | Cart> {
        const { data } = await axios.post<{
            type: 'order' | 'cart'
            data: Order | Cart
        }>(`/carts/${dto.cart_id}/complete`)
        return data.data
    }

    public async listShippingOptionsByCart(
        dto: GetCartDto,
    ): Promise<StoreOption[]> {
        const { data } = await axios.get<HttpResponse<StoreOption[]>>(
            `/shipping/${dto.cart_id}`,
        )

        if (Object.hasOwn(data, 'metadata')) {
            return data.metadata!
        }

        return []
    }

    public async adjustCartAmount(dto: GetCartDto): Promise<Cart> {
        const { data } = await axios.post<HttpResponse<Cart>>(
            `/shipping/${dto.cart_id}`,
        )
        return data.metadata!
    }
}
